import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { useAuthContext } from './AuthContext';
import { supabase } from '@/lib/supabase';
import { toast } from 'sonner';

// Define types for our permission system
export type Permission = {
  module_name: string;
  module_route: string;
  action: string;
};

export type Role = {
  id: string;
  name: string;
  description: string | null;
  is_system: boolean;
};

export type Module = {
  id: string;
  name: string;
  description: string | null;
  route: string | null;
  icon: string | null;
  parent_id: string | null;
  order_index: number;
};

// Type for the role data returned from Supabase
type UserRoleData = {
  role: Role;
};

// Módulo IDs importantes para acesso em toda a aplicação
export const MODULE_IDS = {
  COURSES: '0dea861f-e96a-4d31-8c8c-b1dd7e8af0c3'
};

type PermissionContextType = {
  userPermissions: Permission[];
  userRoles: Role[];
  modules: Module[];
  loading: boolean;
  hasPermission: (moduleName: string, action: string) => boolean;
  hasPermissionById: (moduleId: string, action: string) => boolean;
  refreshPermissions: () => Promise<void>;
  isAdmin: boolean;
};

// Create context with undefined default value
const PermissionContext = createContext<PermissionContextType | undefined>(undefined);

export function PermissionProvider({ children }: { children: ReactNode }) {
  const { user } = useAuthContext();
  const [userPermissions, setUserPermissions] = useState<Permission[]>([]);
  const [userRoles, setUserRoles] = useState<Role[]>([]);
  const [modules, setModules] = useState<Module[]>([]);
  const [loading, setLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);

  // Function to fetch user's permissions
  const fetchUserPermissions = async () => {
    if (!user?.id) {
      setUserPermissions([]);
      setUserRoles([]);
      setIsAdmin(false);
      setLoading(false);
      return;
    }

    try {
      // Fetch user permissions using the stored function
      const { data: permissions, error: permissionsError } = await supabase.rpc<Permission>(
        'get_user_permissions',
        { p_user_id: user.id }
      );

      if (permissionsError) throw permissionsError;
      setUserPermissions(permissions || []);

      // Fetch user roles
      const { data: roleData, error: rolesError } = await supabase
        .from('user_roles')
        .select(`
          role:roles (
            id, 
            name, 
            description, 
            is_system
          )
        `)
        .eq('user_id', user.id);

      if (rolesError) throw rolesError;
      
      // Transform the nested data structure
      if (roleData) {
        const roles: Role[] = roleData.map((item: UserRoleData) => item.role);
        setUserRoles(roles);
        
        // Check if user is admin
        const adminRole = roles.find(role => role.name === 'Admin');
        setIsAdmin(!!adminRole);
      }

    } catch (error) {
      console.error('Error fetching user permissions:', error);
    } finally {
      setLoading(false);
    }
  };

  // Function to fetch all modules
  const fetchModules = async () => {
    try {
      const { data, error } = await supabase
        .from('modules')
        .select('*')
        .order('order_index');

      if (error) throw error;
      
      setModules(data || []);
    } catch (error) {
      console.error('Error fetching modules:', error);
    }
  };

  // Refresh permissions function that can be called from outside
  const refreshPermissions = async () => {
    setLoading(true);
    await Promise.all([fetchUserPermissions(), fetchModules()]);
    setLoading(false);
  };

  // Check if user has a specific permission
  const hasPermission = (moduleName: string, action: string): boolean => {
    if (isAdmin) return true; // Admin has all permissions
    
    return userPermissions.some(
      perm => perm.module_name === moduleName && perm.action === action
    );
  };

  // Check if user has a specific permission by module ID
  const hasPermissionById = (moduleId: string, action: string): boolean => {
    if (isAdmin) return true; // Admin has all permissions
    
    // Find the module name by ID
    const module = modules.find(m => m.id === moduleId);
    if (!module) return false;
    
    return userPermissions.some(
      perm => perm.module_name === module.name && perm.action === action
    );
  };

  // Load permissions and modules when user changes
  useEffect(() => {
    refreshPermissions();
  }, [user?.id]);

  return (
    <PermissionContext.Provider
      value={{
        userPermissions,
        userRoles,
        modules,
        loading,
        hasPermission,
        hasPermissionById,
        refreshPermissions,
        isAdmin
      }}
    >
      {children}
    </PermissionContext.Provider>
  );
}

// Custom hook to use the permission context
export function usePermissions() {
  const context = useContext(PermissionContext);
  if (context === undefined) {
    throw new Error('usePermissions must be used within a PermissionProvider');
  }
  return context;
} 